import React, { useState } from "react";
import { Radio, Box, Typography, Button } from "@mui/material";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RedoIcon from "@mui/icons-material/Redo";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DiagnosisCodeDialog from "components/shared/Dialogs/DiagnosisCodeDialog";
import ProcedureCodeDialog from "components/shared/Dialogs/ProcedureCodeDialog";
import _, { isArray } from "lodash";
import PreviewIcon from "@mui/icons-material/Preview";
import Tooltip from "@mui/material/Tooltip";
import { useContext } from "react";
import { FeatureFlagContext } from "context";
import { useDispatch } from "react-redux";
import { setDialog, setLoading } from "slices/appSlice";
import styles from "./PolicyTableComponent.module.css";
import StyledTooltip from "components/Tooltip";
import StyledDataGrid from "styles/styledDataGrid";
import { useLocation } from "react-router-dom";
import { Tornado } from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";
import {
  formErrorStrfromObj,
  sanitizeCC,
  compareCCValues,
  showVarianceIcon,
} from "utils/shared";
import { updateActivityTrackerArrayforClob } from "utils/ClobDataUserActivityTracker";
import { WORKFLOW_CODING } from "constants/Constants";
import Api from "api";
import StyledTornadoIcon from "components/TornadoIcon";

const other = {
  showCellVerticalBorder: true,
  showColumnVerticalBorder: true,
};

const openPopup = (type, value) => {
  // Logic to open a popup based on the type and value
  console.log(`Open popup for ${type} with value ${value}`);
};

const PolicyTableComponent = ({
  workspaceController,
  editable,
  gridHeight,
  filteredRows,
  showValidationErrors,
  handleValidate,
  policyNumber,
  activityUpdates,
  setActivityUpdates,
  setWorkspaceController = () => {},
}) => {
  const [editingRowId, setEditingRowId] = useState(null);
  const [isPxDialogOpen, setIsPxDialogOpen] = useState(false);
  const [openDxDialog, setOpenDxDialog] = useState(false);
  const [dxDialogReadOnly, setDxDialogReadOnly] = useState(false);
  const [dxCodes, setDxCodes] = useState([]);
  const dispatch = useDispatch();
  const featureFlags = useContext(FeatureFlagContext);
  const { state } = useLocation();

  const selectedHealthPlan = state?.policyState?.healthPlanName;
  const healthPlanDefaultId = state?.policyState?.healthPlanDefaultId;
  const policyTitle = state?.policyTitle;
  const policyVersion = state?.policyVersion;

  const handleAddProcedureCode = () => {
    setIsPxDialogOpen(true);
  };

  const addRowsToGrid = (pxCodeList) => {
    // Prepare new rows for new codes
    const newRowId = uuidv4();
    const ruleVersion =
      selectedHealthPlan === "AHCS"
        ? (1).toFixed(1)
        : "1.0-" + healthPlanDefaultId + "-1.0";
    let activityUpdatesArray = activityUpdates || [];
    const newRows = pxCodeList.map((item) => {
      activityUpdatesArray = updateActivityTrackerArrayforClob(
        workspaceController?.workspace?.healthPlanKey,
        workspaceController?.workspace?.policyKey,
        "200",
        WORKFLOW_CODING,
        window.location.href,
        ruleVersion,
        "", // Change Control Number is added later via the workspace controller
        "", // Rule Name is not added via the coding page.
        item.code,
        "add",
        "procedureCode",
        activityUpdatesArray
      );

      return {
        PolicyHeader: {
          PolicyNumber: policyNumber,
          HealthPlanId: healthPlanDefaultId,
          policyDescription: policyTitle,
          policyVersion: policyVersion,
        },
        RuleHeader: {
          // RuleFlowGroup needs to be read from the workspace ClOB
          ChangeControl: "", //value is added later via the workspace controller
          EditRule: selectedHealthPlan === "AHCS" ? "NEW" : "VARIANCE",
          RuleVersion: ruleVersion,
        },
        RuleFacts: {
          ProcedureCode: item.code,
          ProcedureCodeDescription: item.description,
          CoverageCriteriaList: "",
          CodingClassification: "",
          actions: "Edit/Delete",
          DiagnosisListCondensed: "",
          DiagnosisListExpanded: "",
        },
        tempId: newRowId,
        tempIdList: [newRowId],
        // Initialize other fields as necessary
      };
    });

    setActivityUpdates(activityUpdatesArray);

    setWorkspaceController(
      workspaceController.cloneController().addBusinessRule(newRows)
    );

    // Add the new rows to the current rows
  };

  const handleDeleteRow = (id) => {
    const index = workspaceController.clob.PolicyBusinessRulesList.findIndex(
      (p) => p.index === id
    );
    if (["NEW", "VARIANCE"].includes(workspaceController.getEditRule(index))) {
      let activityUpdatesArray = activityUpdates || [];
      const procedureCode = workspaceController.getProcedureCode(index);
      activityUpdatesArray = updateActivityTrackerArrayforClob(
        workspaceController?.workspace?.healthPlanKey,
        workspaceController?.workspace?.policyKey,
        "200",
        WORKFLOW_CODING,
        window.location.href,
        workspaceController.getRuleVersion(index),
        workspaceController.getChangeControl(index),
        workspaceController.getPolicyBusinessRuleName(index),
        procedureCode,
        "remove",
        "procedureCode",
        activityUpdatesArray
      );
      setActivityUpdates(activityUpdatesArray);
      setWorkspaceController(
        workspaceController.cloneController().removeRule(index)
      );
    } else {
      // If the rule is not in NEW or VARIANCE state, we need to set it to DELETE
      let activityUpdatesArray = activityUpdates || [];
      const procedureCode = workspaceController.getProcedureCode(index);
      activityUpdatesArray = updateActivityTrackerArrayforClob(
        workspaceController?.workspace?.healthPlanKey,
        workspaceController?.workspace?.policyKey,
        "200",
        WORKFLOW_CODING,
        window.location.href,
        workspaceController.getRuleVersion(index),
        workspaceController.getChangeControl(index),
        workspaceController.getPolicyBusinessRuleName(index),
        procedureCode,
        "remove",
        "procedureCode",
        activityUpdatesArray
      );
      setActivityUpdates(activityUpdatesArray);
      setWorkspaceController(
        workspaceController.cloneController().setEditRule(index, "DELETE")
      );
    }
  };

  const handleDuplicateRow = (id) => {
    const index = workspaceController.clob.PolicyBusinessRulesList.findIndex(
      (p) => p.index === id
    );
    const editRule = selectedHealthPlan === "AHCS" ? "NEW" : "VARIANCE";
    const duplicateIndex = index + 1;
    setWorkspaceController(
      workspaceController
        .cloneController()
        .duplicateRule(index)
        .setEditRule(duplicateIndex, editRule)
        .incrementRuleVersion(duplicateIndex)
    );
  };

  const handleRadioChange = (id, classificationValue) => {
    const index = workspaceController.clob.PolicyBusinessRulesList.findIndex(
      (p) => p.index === id
    );
    const editRule = selectedHealthPlan === "AHCS" ? "NEW" : "VARIANCE";

    if (["NEW", "VARIANCE"].includes(workspaceController.getEditRule(index))) {
      const newController = workspaceController
        .cloneController()
        .setClassification(index, classificationValue);
      setWorkspaceController(newController);
    } else {
      const duplicateIndex = index + 1;
      const newController = workspaceController
        .cloneController()
        .setEditRule(index, "EDIT")
        .duplicateRule(index)
        .setEditRule(duplicateIndex, editRule)
        .incrementRuleVersion(duplicateIndex)
        .setClassification(duplicateIndex, classificationValue)
        .setRuleEffectiveDate(duplicateIndex, "");
      setWorkspaceController(newController);
    }
  };

  // Renders a radio button in a cell
  const renderRadioButton = (params, classificationValue) => (
    <Box display="flex" alignItems="center">
      <Radio
        disabled={!editable || params.row.editRule === "EDIT"}
        checked={params.row.codingClassification === classificationValue}
        onChange={() =>
          handleRadioChange(params.row.index, classificationValue)
        }
      />
    </Box>
  );

  const handleAddPxCodesToGrid = (pxCodes) => {
    addRowsToGrid(pxCodes);
    setOpenDxDialog(false);
  };

  const handleOpenDxDialog = (rowId, dxCodes, readOnly) => {
    setDxDialogReadOnly(readOnly);
    setEditingRowId(rowId);
    setDxCodes(dxCodes);
    setOpenDxDialog(true);
  };

  const fetchCollapsedRange = async (codes) => {
    if (!codes.length) {
      return [];
    }
    dispatch(setLoading(true));
    try {
      const response = await Api.policyCoding.getDxCollapsed(codes);
      const list = response.data;
      return list.length ? list : [];
    } catch (error) {
      const message =
        formErrorStrfromObj(error) || "An error occurred during the search.";
      dispatch(setDialog({ message, title: "Error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSaveDx = async (dxCodes) => {
    const codes = dxCodes.map((item) => item.code);
    const dxCodesStr = codes.join(",");
    const condenseList = await fetchCollapsedRange(codes);
    const condenseStr = condenseList?.join(",");
    const index = workspaceController.clob.PolicyBusinessRulesList.findIndex(
      (p) => p.index === editingRowId
    );
    const currentDiagnosisList =
      workspaceController.getDiagnosisListExpanded(index) || [];
    const splitList = String(currentDiagnosisList || "").split(",");
    let codesRemoved = [];
    const codesAdded = codes.filter((code) => !splitList.includes(code));
    let activityUpdatesArray = activityUpdates || [];
    if (currentDiagnosisList.length > 0) {
      codesRemoved = splitList.filter((code) => !dxCodesStr.includes(code));
    }
    if (codesAdded.length > 0) {
      activityUpdatesArray = updateActivityTrackerArrayforClob(
        workspaceController?.workspace?.healthPlanKey,
        workspaceController?.workspace?.policyKey,
        "200",
        WORKFLOW_CODING,
        window.location.href,
        workspaceController.getRuleVersion(index),
        workspaceController.getChangeControl(index),
        workspaceController.getPolicyBusinessRuleName(index),
        codesAdded.join(","),
        "add",
        "diagnosisCode",
        activityUpdatesArray
      );
    }
    if (codesRemoved.length > 0) {
      activityUpdatesArray = updateActivityTrackerArrayforClob(
        workspaceController?.workspace?.healthPlanKey,
        workspaceController?.workspace?.policyKey,
        "200",
        WORKFLOW_CODING,
        window.location.href,
        workspaceController.getRuleVersion(index),
        workspaceController.getChangeControl(index),
        workspaceController.getPolicyBusinessRuleName(index),
        codesRemoved.join(","),
        "remove",
        "diagnosisCode",
        activityUpdatesArray
      );
    }
    setActivityUpdates(activityUpdatesArray);
    const editRule = selectedHealthPlan === "AHCS" ? "NEW" : "VARIANCE";
    if (["NEW", "VARIANCE"].includes(workspaceController.getEditRule(index))) {
      const newController = workspaceController
        .cloneController()
        .setDiagnosisListExpanded(index, dxCodesStr)
        .setDiagnosisListCondensed(index, condenseStr);
      setWorkspaceController(newController);
    } else {
      const duplicateIndex = index + 1;
      const newController = workspaceController
        .cloneController()
        .setEditRule(index, "EDIT")
        .duplicateRule(index)
        .setEditRule(duplicateIndex, editRule)
        .incrementRuleVersion(duplicateIndex)
        .setDiagnosisListExpanded(duplicateIndex, dxCodesStr)
        .setDiagnosisListCondensed(duplicateIndex, condenseStr)
        .setRuleEffectiveDate(duplicateIndex, "");
      setWorkspaceController(newController);
    }

    setOpenDxDialog(false);
  };

  const renderDxCodes = (params) =>
    params.value ? (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ height: "100%" }}
      >
        <IconButton
          onClick={() =>
            handleOpenDxDialog(
              params.row.index,
              params.value,
              !editable || params.row.editRule === "EDIT"
            )
          }
        >
          <Tooltip
            title={
              !editable || params.row.editRule === "EDIT"
                ? "View Diagnoses"
                : "View or Add Diagnoses"
            }
          >
            <PreviewIcon
              sx={{
                color:
                  !editable || params.row.editRule === "EDIT"
                    ? "grey"
                    : "primary.main",
              }}
            />
          </Tooltip>
        </IconButton>
      </Box>
    ) : params.row?.errors?.dxCodes && showValidationErrors ? (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ height: "100%" }}
      >
        <StyledTooltip
          open={!!params.row?.errors?.dxCodes}
          title={params.row?.errors?.dxCodes}
        >
          <IconButton
            onClick={() =>
              handleOpenDxDialog(
                params.row.index,

                params.value,
                !editable || params.row.editRule === "EDIT"
              )
            }
          >
            <AddCircleOutlineIcon />
          </IconButton>
        </StyledTooltip>
      </Box>
    ) : (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ height: "100%" }}
      >
        <Tooltip title="Add diagnosis">
          <IconButton
            onClick={() =>
              handleOpenDxDialog(
                params.row.index,

                params.value,
                !editable || params.row.editRule === "EDIT"
              )
            }
          >
            <AddCircleOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  const columns = [
    {
      field: "code",
      headerName: "Code",
      headerAlign: "center",
      flex: 1,
      renderCell: (params) => (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <Typography>{params.value}</Typography>
          {editable && params.row.editRule !== "EDIT" && (
            <>
              <IconButton
                aria-label="duplicate"
                size="small"
                onClick={() => handleDuplicateRow(params.row.index)}
              >
                <RedoIcon fontSize="small" />
              </IconButton>
              {/* This is used to identify variances in new rules and rules that
              have completed the sheet generation process */}
              {showVarianceIcon(params.row.RuleHeader, selectedHealthPlan) && (
                <StyledTornadoIcon />
              )}
            </>
          )}
        </Box>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 4,
    },
    {
      field: "ccNumber",
      headerName: "CC#",
      headerAlign: "center",
      flex: 1,
      align: "center",
      editable: editable,
      cellClassName: (params) =>
        params.row?.errors?.cc && showValidationErrors
          ? styles["error-cell"]
          : "",
      renderCell: (params) => {
        if (params.row?.errors?.cc && showValidationErrors)
          return params.row?.errors?.cc;

        // Logic to filter duplicate values from combined rules
        const values = params.value ? params.value.split(",") : [];
        const uniqueValues = [...new Set(values)];
        return uniqueValues.join(", ");
      },
    },

    // Classification column that groups PA Req'd, PA Not Req'd, and Not Covered
    {
      field: "classification",
      headerName: "Classification",
      flex: 2.2,
      headerAlign: "center",
      renderHeader: () => (
        <Box display="flex" justifyContent="center" gap={4}>
          <Box flex={1} display="flex" flexDirection="column">
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              PA Reqd
            </Typography>
          </Box>
          <Box flex={1} display="flex" flexDirection="column">
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              PA Not Reqd
            </Typography>
          </Box>
          <Box flex={1} display="flex" flexDirection="column">
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              Not Covered
            </Typography>
          </Box>
        </Box>
      ),
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" gap={4}>
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            {renderRadioButton(params, "paReqd")}
          </Box>
          <Box flex={1} display="flex" flexDirection="column">
            {renderRadioButton(params, "paNotReqd")}
          </Box>
          <Box flex={1} display="flex" flexDirection="column">
            {renderRadioButton(params, "notCovered")}
          </Box>
        </Box>
      ),
      // Apply the error-cell class if there's a classification error
      cellClassName: (params) =>
        params.row?.errors?.classification && showValidationErrors
          ? styles["error-cell"]
          : "",
    },

    {
      field: "dxCodes",
      headerName: "DX Codes",
      headerAlign: "center",
      flex: 1,
      cellClassName: (params) =>
        params.row?.errors?.dxCodes && showValidationErrors
          ? styles["error-cell"]
          : "",
      renderCell: (params) =>
        params.row?.errors?.dxCodes && showValidationErrors
          ? params.row?.errors?.dxCodes
          : renderDxCodes(params),
      renderEditCell: renderDxCodes,
      editable: editable,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          {editable && (
            <Tooltip title="copy content">
              <IconButton aria-label="edit">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          )}
          {editable && (
            <Tooltip title="delete">
              <IconButton
                aria-label="delete"
                disabled={!editable}
                onClick={() => handleDeleteRow(params.row.index)}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const zCodeColumn = {
    field: "zCodes",
    headerName: "Z Codes",
    flex: 0.8,
    renderCell: (params) => (
      <Link href="#" onClick={() => openPopup("zCodes", params.value)}>
        {params.value}
      </Link>
    ),
  };
  const PAAColumn = {
    field: "paaQuestions",
    headerName: "PAA Questions",
    flex: 1,
    renderCell: (params) => (
      <Link href="#" onClick={() => openPopup("paaQuestions", params.value)}>
        {params.value}
      </Link>
    ),
  };

  if (featureFlags && featureFlags.length) {
    const pgtmFlag = featureFlags.find(
      (flag) => flag.featureSwitchName === "IsPGTMEnabled"
    );
    if (pgtmFlag.enabledIndicator) {
      columns.splice(columns.length - 1, 0, zCodeColumn);
      columns.splice(columns.length - 1, 0, PAAColumn);
    }
  }

  const columnGroupingModel = [
    {
      groupId: "Classification",
      headerAlign: "center",
      description: "Classification",
      children: [
        { field: "paReqd" },
        { field: "paNotReqd" },
        { field: "notCovered" },
      ],
    },
  ];

  const handleProcessRowUpdate = (updatedRow, originalRow) => {
    const { index } = updatedRow;
    const editRule = selectedHealthPlan === "AHCS" ? "NEW" : "VARIANCE";
    let newCC = updatedRow.ccNumber;
    let originalRowCC = originalRow.ccNumber;

    if (typeof newCC === "string") {
      newCC = newCC.trim();
    }
    newCC = sanitizeCC(newCC);
    const ccChanges = compareCCValues(originalRowCC, newCC);
    let activityUpdatesArray = activityUpdates || [];
    if (ccChanges.added.length > 0) {
      activityUpdatesArray = updateActivityTrackerArrayforClob(
        workspaceController?.workspace?.healthPlanKey,
        workspaceController?.workspace?.policyKey,
        "200",
        WORKFLOW_CODING,
        window.location.href,
        workspaceController.getRuleVersion(index),
        workspaceController.getChangeControl(index),
        workspaceController.getPolicyBusinessRuleName(index),
        ccChanges.added.join(","),
        "add",
        "Coverage Criteria",
        activityUpdatesArray
      );
    }
    if (ccChanges.removed.length > 0) {
      activityUpdatesArray = updateActivityTrackerArrayforClob(
        workspaceController?.workspace?.healthPlanKey,
        workspaceController?.workspace?.policyKey,
        "200",
        WORKFLOW_CODING,
        window.location.href,
        workspaceController.getRuleVersion(index),
        workspaceController.getChangeControl(index),
        workspaceController.getPolicyBusinessRuleName(index),
        ccChanges.removed.join(","),
        "remove",
        "Coverage Criteria",
        activityUpdatesArray
      );
    }
    setActivityUpdates(activityUpdatesArray);
    if (originalRow.ccNumber !== newCC) {
      if (
        ["NEW", "VARIANCE"].includes(workspaceController.getEditRule(index))
      ) {
        const newController = workspaceController
          .cloneController()
          .setCoverageCriteriaList(index, newCC);
        setWorkspaceController(newController);
      } else {
        const duplicateIndex = index + 1;
        const newController = workspaceController
          .cloneController()
          .setEditRule(index, "EDIT")
          .duplicateRule(index)
          .setEditRule(duplicateIndex, editRule)
          .incrementRuleVersion(duplicateIndex)
          .setCoverageCriteriaList(duplicateIndex, newCC)
          .setRuleEffectiveDate(duplicateIndex, "");
        setWorkspaceController(newController);
      }
    }

    return updatedRow;
  };
  const onDxDialogClose = () => {
    setOpenDxDialog(false);
    setDxDialogReadOnly(false);
  };
  return (
    <div>
      {editable && (
        <Box>
          <Button
            onClick={handleAddProcedureCode}
            data-testid="add-procedure-code-button"
            variant="text"
            startIcon={<AddCircleIcon color="primary" />}
          >
            Add Procedure Code
          </Button>
          <Button onClick={handleValidate} variant="text">
            Validate
          </Button>
        </Box>
      )}
      <StyledDataGrid
        sx={{
          maxHeight: gridHeight - (editable ? 115 : 80),
        }}
        rows={filteredRows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        disableColumnSelector
        disableColumnMenu
        rowHeight={40}
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(error) =>
          dispatch(setDialog({ message: error.message, title: "Error" }))
        }
        isCellEditable={(params) => params.row.editRule !== "EDIT"}
        {...other}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[10, 25, 50]}
      />
      {openDxDialog && (
        <DiagnosisCodeDialog
          open={openDxDialog}
          dxCodes={dxCodes}
          onClose={() => onDxDialogClose()}
          onSave={handleSaveDx}
          readOnly={dxDialogReadOnly}
        />
      )}
      <ProcedureCodeDialog
        open={isPxDialogOpen}
        onClose={() => setIsPxDialogOpen(false)}
        onSave={handleAddPxCodesToGrid}
      />
    </div>
  );
};

export default PolicyTableComponent;
